/**
 * Transportes de impressão — ABSTRAÍDOS da marca: o mesmo ESC/POS vai por Bluetooth (Web
 * Bluetooth, Chrome Android), cabo/USB-serial (Web Serial), app RawBT (Android) ou pelo
 * navegador (imprimir/salvar PDF). Imprimir NUNCA emite nada: falha aqui só é registrada.
 */
import type { PrintLayout } from "@cafegestor/shared";
import { toEscPos, toHtml } from "./escpos";

export type Transport =
  "web_bluetooth" | "web_serial" | "rawbt" | "navegador" | "pdf" | "compartilhar";

export const TRANSPORT_LABEL: Record<Transport, string> = {
  web_bluetooth: "Impressora Bluetooth",
  web_serial: "Impressora USB/serial",
  rawbt: "App RawBT (Android)",
  navegador: "Imprimir pelo navegador",
  pdf: "Salvar PDF",
  compartilhar: "Compartilhar",
};

// Tipos mínimos das APIs experimentais (não estão na biblioteca DOM do TypeScript)
type GattCharacteristic = {
  properties: { write: boolean; writeWithoutResponse: boolean };
  writeValue(data: BufferSource): Promise<void>;
  writeValueWithoutResponse?: (data: BufferSource) => Promise<void>;
};
type GattService = { getCharacteristics(): Promise<GattCharacteristic[]> };
type GattServer = {
  connect(): Promise<GattServer>;
  getPrimaryServices(): Promise<GattService[]>;
  disconnect(): void;
};
type BluetoothDeviceLike = { gatt?: GattServer; name?: string };
type BluetoothLike = {
  requestDevice(opts: {
    acceptAllDevices?: boolean;
    optionalServices?: string[];
  }): Promise<BluetoothDeviceLike>;
};
type SerialPortLike = {
  open(opts: { baudRate: number }): Promise<void>;
  writable: WritableStream<Uint8Array> | null;
  close(): Promise<void>;
};
type SerialLike = { requestPort(): Promise<SerialPortLike> };

/** Serviços GATT usados por impressoras térmicas comuns (genéricos, não de uma marca). */
const PRINTER_SERVICES = [
  "000018f0-0000-1000-8000-00805f9b34fb",
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2",
  "49535343-fe7d-4ae5-8fa9-9fafd205e455",
  "0000ff00-0000-1000-8000-00805f9b34fb",
];

const nav = () => navigator as Navigator & { bluetooth?: BluetoothLike; serial?: SerialLike };

export function availableTransports(): Transport[] {
  const out: Transport[] = [];
  if (typeof navigator !== "undefined" && nav().bluetooth) out.push("web_bluetooth");
  if (typeof navigator !== "undefined" && nav().serial) out.push("web_serial");
  if (typeof navigator !== "undefined" && /Android/i.test(navigator.userAgent)) out.push("rawbt");
  out.push("navegador", "pdf");
  if (typeof navigator !== "undefined" && "share" in navigator) out.push("compartilhar");
  return out;
}

let bluetoothCache: GattCharacteristic | null = null;

async function bluetoothWrite(bytes: Uint8Array) {
  const bt = nav().bluetooth;
  if (!bt) throw new Error("Este navegador não tem Bluetooth (use o Chrome no Android).");
  if (!bluetoothCache) {
    const device = await bt.requestDevice({
      acceptAllDevices: true,
      optionalServices: PRINTER_SERVICES,
    });
    const server = await device.gatt?.connect();
    if (!server) throw new Error("Não foi possível conectar à impressora.");
    for (const svc of await server.getPrimaryServices()) {
      for (const ch of await svc.getCharacteristics()) {
        if (ch.properties.write || ch.properties.writeWithoutResponse) {
          bluetoothCache = ch;
          break;
        }
      }
      if (bluetoothCache) break;
    }
    if (!bluetoothCache) throw new Error("A impressora não aceita impressão por este canal.");
  }
  const ch = bluetoothCache;
  try {
    for (let i = 0; i < bytes.length; i += 180) {
      const chunk = bytes.slice(i, i + 180);
      if (ch.properties.writeWithoutResponse && ch.writeValueWithoutResponse)
        await ch.writeValueWithoutResponse(chunk);
      else await ch.writeValue(chunk);
    }
  } catch (err) {
    bluetoothCache = null; // reconecta na próxima tentativa
    throw err;
  }
}

async function serialWrite(bytes: Uint8Array) {
  const serial = nav().serial;
  if (!serial) throw new Error("Este navegador não tem acesso a portas seriais.");
  const port = await serial.requestPort();
  await port.open({ baudRate: 9600 });
  try {
    const writer = port.writable?.getWriter();
    if (!writer) throw new Error("Porta sem escrita.");
    try {
      await writer.write(bytes);
    } finally {
      writer.releaseLock(); // sempre: senão a porta não fecha e a próxima impressão falha
    }
  } finally {
    // fechar não pode esconder o erro original da impressora
    await port.close().catch(() => undefined);
  }
}

function toBase64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

/** Imprime pela janela do navegador; espera a folha de estilo (mesma origem) carregar. */
function printHtml(html: string): Promise<void> {
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc) throw new Error("Não foi possível preparar a impressão.");
  doc.open();
  doc.write(html);
  doc.close();
  return new Promise((resolve) => {
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      setTimeout(() => frame.remove(), 60_000);
      resolve();
    };
    const link = doc.querySelector("link[rel=stylesheet]");
    if (link) {
      link.addEventListener("load", go);
      link.addEventListener("error", go);
      setTimeout(go, 3000);
    } else go();
  });
}

/** Envia o layout pelo transporte escolhido. Lança erro se a impressora falhar. */
export async function printLayout(layout: PrintLayout, transport: Transport): Promise<void> {
  switch (transport) {
    case "web_bluetooth":
      return bluetoothWrite(toEscPos(layout));
    case "web_serial":
      return serialWrite(toEscPos(layout));
    case "rawbt":
      window.location.href = `rawbt:base64,${toBase64(toEscPos(layout))}`;
      return;
    case "navegador":
    case "pdf":
      // "Salvar como PDF" é escolhido na janela de impressão do navegador
      return printHtml(
        toHtml(layout, { stylesheetHref: `${window.location.origin}/print-receipt.css` }),
      );
    case "compartilhar": {
      const file = new File([toHtml(layout)], `${layout.title.replace(/\W+/g, "-")}.html`, {
        type: "text/html",
      });
      const share = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
      if (share.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: layout.title });
      } else {
        const url = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
      }
      return;
    }
  }
}
